import React from 'react';
import * as Sentry from '@sentry/react';
import { captureError } from '../lib/posthog';

// Custom fallback UI for Sentry ErrorBoundary
const FallbackComponent = ({ error, resetError }) => {
  const errorId = error?.eventId || 'unknown';
  
  // Automatically report to Sentry and PostHog
  React.useEffect(() => {
    if (error) {
      // Report to Sentry
      if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.setContext('error_boundary', {
          errorId,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        });
      }
      
      // Report to PostHog
      captureError(error, {
        error_category: 'REACT_ERROR_BOUNDARY',
        error_id: errorId,
        component_stack: error.componentStack,
        error_boundary: true,
      });
    }
  }, [error, errorId]);

  return (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '2rem',
    textAlign: 'center',
    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
  }}>
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '2rem',
      maxWidth: '600px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#dc2626' }}>
        Something went wrong
      </h1>
      <p style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: '1rem' }}>
        We're sorry for the inconvenience. The error has been automatically reported and we'll look into it.
      </p>
      {errorId && errorId !== 'unknown' && (
        <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '2rem' }}>
          Error ID: <code style={{ 
            background: '#f3f4f6', 
            padding: '0.25rem 0.5rem', 
            borderRadius: '4px',
            fontFamily: 'monospace'
          }}>{errorId}</code>
        </p>
      )}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => {
            resetError();
            window.location.reload();
          }}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
        >
          Reload Page
        </button>
        <button
          onClick={() => {
            resetError();
            window.location.href = '/';
          }}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'white',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
          onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
        >
          Go to Homepage
        </button>
      </div>
    {import.meta.env.DEV && error && (
      <details style={{ marginTop: '2rem', textAlign: 'left', maxWidth: '600px' }}>
        <summary style={{ cursor: 'pointer', color: '#64748b' }}>Error Details (Development)</summary>
        <pre style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '0.5rem',
            overflow: 'auto',
            fontSize: '0.875rem',
            maxHeight: '400px'
          }}>
          {error.toString()}
          {error.stack && `\n\n${error.stack}`}
        </pre>
      </details>
      )}
    </div>
    </div>
  );
};

// Use Sentry's ErrorBoundary component
const ErrorBoundary = ({ children }) => {
  return (
    <Sentry.ErrorBoundary fallback={FallbackComponent} showDialog={false}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;

